import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const ProductCard = ({ product, index, onClick }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [imgIdx, setImgIdx] = useState(0);
    const [failedCount, setFailedCount] = useState(0);
    const [isPermanentlyFailed, setIsPermanentlyFailed] = useState(false);

    const getImages = () => {
        try {
            let data = product.images;
            if (typeof data === 'string') data = JSON.parse(data);

            if (Array.isArray(data)) {
                return data.map(img => {
                    if (!img) return '';
                    // If the path already has http, return it as is
                    if (img.startsWith('http')) return img;

                    // Combines API base (localhost or live domain) with the relative disk path
                    return `${API_BASE_URL}${img}`;
                });
            }
            return [];
        } catch (e) {
            return [];
        }
    };

    const images = getImages();
    const currentImgUrl = images[imgIdx];

    // Reset states when image index changes
    useEffect(() => {
        setIsLoaded(false);
        setFailedCount(0);
        setIsPermanentlyFailed(false);
    }, [imgIdx]);

    // Slideshow interval
    useEffect(() => {
        if (images.length <= 1) return;
        const interval = setInterval(() => {
            setImgIdx(prev => (prev + 1) % images.length);
        }, 4000);
        return () => clearInterval(interval);
    }, [images]);

    const handleImageError = (e) => {
        if (failedCount < 3) {
            // Increment fail count and wait 1.5 seconds before attempting a controlled retry
            setFailedCount(prev => prev + 1);
            setTimeout(() => {
                if (e.target) {
                    e.target.src = `${currentImgUrl}?retry=${Date.now()}`;
                }
            }, 1500);
        } else {
            // Max 3 retries reached: stop completely and show fallback "No Signal"
            setIsPermanentlyFailed(true);
            setIsLoaded(true);
        }
    };

    return (
        <motion.div 
            whileHover={{ y: -8 }}
            onClick={() => onClick(product)}
            className="group relative cursor-pointer bg-white/5 dark:bg-white/[0.03] rounded-[2.5rem] p-4 border border-white/10 backdrop-blur-sm overflow-hidden"
        >
            {/* --- IMAGE CONTAINER + SKELETON --- */}
            <div className="relative h-64 overflow-hidden rounded-[2rem] bg-black/5 flex items-center justify-center p-6">
                
                {/* 1. Shimmering Skeleton */}
                {!isLoaded && !isPermanentlyFailed && (
                    <div className="absolute inset-0 z-10 bg-white/5 dark:bg-white/10 animate-pulse flex items-center justify-center">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full animate-[shimmer_2s_infinite]" />
                        <div className="w-10 h-10 border-2 border-red-600/20 border-t-red-600 rounded-full animate-spin" />
                    </div>
                )}

                {/* 2. Actual Image or Fallback */}
                <AnimatePresence>
                        {images.length > 0 && !isPermanentlyFailed ? (
                            <motion.img 
                                key={`${currentImgUrl}-${imgIdx}`}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.8, ease: "easeInOut" }}
                                src={currentImgUrl} 
                                onLoad={() => setIsLoaded(true)}
                                onError={handleImageError}
                                className="absolute inset-0 w-full h-full object-cover"
                                alt={product.model_name}
                            />
                        ) : (
                            <div className="opacity-40 text-[10px] font-black uppercase tracking-widest text-gray-400">No Signal</div>
                        )}
                    </AnimatePresence>

                {/* Brand Label */}
                <div className="absolute top-4 left-4 z-20">
                    <span className="bg-red-600 text-white text-[8px] font-black px-3 py-1.5 rounded-full uppercase tracking-widest shadow-lg">
                        {product.Brand?.name || 'SWIFT'}
                    </span>
                </div>
            </div>

            {/* --- PRODUCT INFO --- */}
            <div className="mt-5 px-3">
                <h3 className="text-[15px] font-black truncate text-slate-800 dark:text-white uppercase tracking-tighter mb-2">
                    {product.model_name}
                </h3>
                
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-[9px] font-bold text-gray-400 uppercase tracking-[2px]">Market Value</p>
                        <p className="text-red-600 font-black text-2xl tracking-tighter">
                            <span className="text-xs mr-1 opacity-50">Ksh</span>
                            {Number(product.price).toLocaleString()}
                        </p>
                    </div>
                    
                    <div className="flex flex-col items-end">
                        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                            {product.Category?.name}
                        </span>
                        <div className={`h-1.5 w-10 rounded-full ${product.stock > 0 ? 'bg-green-500/50' : 'bg-red-500/50'}`}>
                            <motion.div 
                                initial={{ width: 0 }}
                                animate={{ width: '100%' }}
                                className={`h-full rounded-full ${product.stock > 0 ? 'bg-green-500' : 'bg-red-500'}`}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default ProductCard;